import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './card';
import { Button } from './button';
import { useCustomTheme } from '../../contexts/ThemeContext';
import oneSpaceApi from '../../lib/onespace-api';

interface WidgetConfig {
  template: string;
  title: string;
  width_column: number;
  [key: string]: any;
}

// Background object structure from the API
interface BackgroundStyle {
  type: string; // "color", "image", "gradient"
  color: string;
  image?: string | null;
  gradient?: string | null;
}

interface StyleSettings {
  light: {
    background: BackgroundStyle;
    text_color: string;
    primary_color: string;
    secondary_color: string;
  };
  dark: {
    background: BackgroundStyle;
    text_color: string;
    primary_color: string;
    secondary_color: string;
  };
  layout: 'centered' | 'left-aligned';
}

interface SettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  currentWidgets: WidgetConfig[];
  onLogout?: () => void;
}

const AVAILABLE_WIDGET_TYPES = [
  { id: 'notes', label: 'Notities' },
  { id: 'rss', label: 'RSS Feed' },
  { id: 'news', label: 'Nieuws' },
  { id: 'image', label: 'Afbeelding' },
  { id: 'gallery', label: 'Galerij' },
  { id: 'pomodoro', label: 'Pomodoro Timer' },
  { id: 'kanban', label: 'Kanban Board' },
  { id: 'timetracker', label: 'Urenregistratie' },
];

export const SettingsDialog: React.FC<SettingsDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  currentWidgets = [],
  onLogout
}) => {
  const { currentThemeColors, setThemeStyles } = useCustomTheme();
  const [widgets, setWidgets] = useState<WidgetConfig[]>(currentWidgets);
  const [activeTab, setActiveTab] = useState<'widgets' | 'styling' | 'layout'>('widgets');
  const [styleSettings, setStyleSettings] = useState<StyleSettings>({
    light: {
      background: {
        type: "color",
        color: "#ffffff",
        image: null,
        gradient: null
      },
      text_color: '#000000',
      primary_color: '#000000',
      secondary_color: '#ff9900',
    },
    dark: {
      background: {
        type: "color",
        color: "#000000",
        image: null,
        gradient: null
      },
      text_color: '#ffffff',
      primary_color: '#ffffff',
      secondary_color: '#ff9900',
    },
    layout: 'centered'
  });
  const [isSaving, setIsSaving] = useState(false);

  // Laad de huidige instellingen
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Widgets
        if (currentWidgets.length > 0) {
          setWidgets(currentWidgets);
        }

        // Styling
        const styleResponse = await oneSpaceApi.get('/api/settings/styling');
        if (styleResponse.data && styleResponse.data.styling) {
          // Check if the API response uses the new background structure
          const apiStyling = styleResponse.data.styling;
          
          // Handle legacy format conversion if needed
          if (apiStyling.light && !apiStyling.light.background) {
            // Convert from old format to new format
            const convertedStyling = {
              light: {
                background: {
                  type: apiStyling.light.background_image ? "image" : "color",
                  color: apiStyling.light.background_color,
                  image: apiStyling.light.background_image || null,
                  gradient: null
                },
                text_color: apiStyling.light.text_color,
                primary_color: apiStyling.light.primary_color,
                secondary_color: apiStyling.light.secondary_color,
              },
              dark: {
                background: {
                  type: apiStyling.dark.background_image ? "image" : "color",
                  color: apiStyling.dark.background_color,
                  image: apiStyling.dark.background_image || null,
                  gradient: null
                },
                text_color: apiStyling.dark.text_color,
                primary_color: apiStyling.dark.primary_color,
                secondary_color: apiStyling.dark.secondary_color,
              },
              layout: 'centered' as const
            };
            setStyleSettings(convertedStyling);
          } else {
            setStyleSettings(apiStyling);
          }
        }
      } catch (error) {
        console.error('Fout bij ophalen instellingen:', error);
      }
    };

    if (isOpen) {
      fetchSettings();
    }
  }, [isOpen, currentWidgets]);

  // Widget toevoegen
  const addWidget = (type: string) => {
    const newWidget: WidgetConfig = {
      template: type,
      title: AVAILABLE_WIDGET_TYPES.find(w => w.id === type)?.label || type,
      width_column: 1,
      // Voeg standaard configuratie toe op basis van widget type
      ...(type === 'rss' && {
        feeds: ["https://www.nu.nl/rss"],
        limit: 5,
        sort: "desc",
        sort_by: "pubDate"
      }),
      ...(type === 'image' && {
        url: "https://via.placeholder.com/800x400"
      }),
      ...(type === 'gallery' && {
        images: ["https://via.placeholder.com/800x400", "https://via.placeholder.com/800x400"],
        show_image_time: 5000
      })
    };

    setWidgets([...widgets, newWidget]);
  };

  // Widget verwijderen
  const removeWidget = (index: number) => {
    const updatedWidgets = [...widgets];
    updatedWidgets.splice(index, 1);
    setWidgets(updatedWidgets);
  };

  // Widget breedte aanpassen
  const toggleWidgetWidth = (index: number) => {
    const updatedWidgets = [...widgets];
    updatedWidgets[index].width_column = updatedWidgets[index].width_column === 1 ? 2 : 1;
    setWidgets(updatedWidgets);
  };

  // Wijzigingen opslaan
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Widgets opslaan
      await oneSpaceApi.post('/api/settings/widgets', {
        widgets: widgets
      });

      // Styling opslaan
      await oneSpaceApi.post('/api/settings/styling', {
        styling: styleSettings
      });

      // Update de lokale styling
      setThemeStyles(styleSettings);
      
      // Sluit het dialoog en ververs het dashboard
      onSave();
      onClose();
    } catch (error) {
      console.error('Fout bij opslaan instellingen:', error);
      alert('Er is een fout opgetreden bij het opslaan van de instellingen.');
    } finally {
      setIsSaving(false);
    }
  };

  // Styling instellingen wijzigen
  const handleStyleChange = (mode: 'light' | 'dark', field: string, value: string | null) => {
    setStyleSettings(prev => {
      if (field === 'layout') {
        return {
          ...prev,
          layout: value as 'centered' | 'left-aligned'
        };
      }
      
      const newSettings = { ...prev };
      
      if (field === 'background_image') {
        newSettings[mode].background.image = value;
      } else if (field === 'background_color') {
        newSettings[mode].background.color = value || '#ffffff';
      } else if (field === 'text_color') {
        newSettings[mode].text_color = value || '#000000';
      } else if (field === 'primary_color') {
        newSettings[mode].primary_color = value || '#ffffff';
      } else if (field === 'secondary_color') {
        newSettings[mode].secondary_color = value || '#ff9900';
      }
      
      return newSettings;
    });
  };

  // Als het dialoog gesloten is, render niets
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="w-[800px] max-h-[90vh] bg-primary-theme border border-theme-10 rounded-lg shadow-xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-theme-10 flex justify-between items-center">
          <h2 className="text-xl font-medium text-theme">Instellingen</h2>
          <Button 
            variant="ghost" 
            size="sm"
            className="text-theme hover:bg-primary-theme-10 rounded-full"
            onClick={onClose}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-theme-10">
          <button 
            className={`px-4 py-2 ${activeTab === 'widgets' ? 'bg-primary-theme-10 text-theme font-medium' : 'text-theme-80'}`}
            onClick={() => setActiveTab('widgets')}
          >
            Widgets
          </button>
          <button 
            className={`px-4 py-2 ${activeTab === 'styling' ? 'bg-primary-theme-10 text-theme font-medium' : 'text-theme-80'}`}
            onClick={() => setActiveTab('styling')}
          >
            Vormgeving
          </button>
          <button 
            className={`px-4 py-2 ${activeTab === 'layout' ? 'bg-primary-theme-10 text-theme font-medium' : 'text-theme-80'}`}
            onClick={() => setActiveTab('layout')}
          >
            Layout
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {activeTab === 'widgets' && (
            <div>
              <h3 className="text-theme font-medium mb-4">Widgets configureren</h3>
              <div className="mb-6">
                <div className="bg-primary-theme-10 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-theme-60">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">Hier kun je straks widgets bewerken en toevoegen</span>
                  </div>
                </div>
              </div>
              <div className="mb-6">
                <h4 className="text-theme mb-2">Widget toevoegen:</h4>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_WIDGET_TYPES.map(widget => (
                    <Button 
                      key={widget.id} 
                      variant="outline" 
                      size="sm"
                      className="text-theme border-theme-20"
                      onClick={() => addWidget(widget.id)}
                    >
                      + {widget.label}
                    </Button>
                  ))}
                </div>
              </div>

              <h4 className="text-theme mb-2">Huidige widgets:</h4>
              <div className="space-y-2">
                {widgets.map((widget, index) => (
                  <div key={`${widget.template}-${index}`} className="bg-primary-theme-10 border border-theme-10 rounded-md p-3 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="text-theme">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                      </div>
                      <div>
                        <div className="text-theme font-medium">{widget.title}</div>
                        <div className="text-theme-80 text-sm">
                          {widget.width_column === 2 ? '2 kolommen' : '1 kolom'}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-theme hover:bg-primary-theme-20 h-8 w-8 p-0 rounded-full"
                        onClick={() => toggleWidgetWidth(index)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                        </svg>
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-theme hover:bg-primary-theme-20 hover:text-destructive h-8 w-8 p-0 rounded-full"
                        onClick={() => removeWidget(index)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              
              {widgets.length === 0 && (
                <div className="text-theme-80 text-center py-4">
                  Geen widgets geconfigureerd. Voeg widgets toe om te beginnen.
                </div>
              )}
            </div>
          )}

          {activeTab === 'styling' && (
            <div>
              <h3 className="text-theme font-medium mb-4">Vormgeving aanpassen</h3>
              
              <div className="grid grid-cols-2 gap-6">
                {/* Light mode */}
                <div className="space-y-4">
                  <h4 className="text-theme font-medium">Light mode</h4>
                  
                  <div>
                    <label className="block text-theme-80 mb-1 text-sm">Achtergrond afbeelding URL</label>
                    <input
                      type="text"
                      className="w-full bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                      value={styleSettings.light.background.image || ''}
                      onChange={(e) => handleStyleChange('light', 'background_image', e.target.value || null)}
                      placeholder="URL van afbeelding (optioneel)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-theme-80 mb-1 text-sm">Achtergrond kleur</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        className="h-8 w-8 rounded cursor-pointer border border-theme-10"
                        value={styleSettings.light.background.color}
                        onChange={(e) => handleStyleChange('light', 'background_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                        value={styleSettings.light.background.color}
                        onChange={(e) => handleStyleChange('light', 'background_color', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-theme-80 mb-1 text-sm">Tekst kleur</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        className="h-8 w-8 rounded cursor-pointer border border-theme-10"
                        value={styleSettings.light.text_color}
                        onChange={(e) => handleStyleChange('light', 'text_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                        value={styleSettings.light.text_color}
                        onChange={(e) => handleStyleChange('light', 'text_color', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-theme-80 mb-1 text-sm">Primaire kleur (widget achtergrond)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        className="h-8 w-8 rounded cursor-pointer border border-theme-10"
                        value={styleSettings.light.primary_color}
                        onChange={(e) => handleStyleChange('light', 'primary_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                        value={styleSettings.light.primary_color}
                        onChange={(e) => handleStyleChange('light', 'primary_color', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-theme-80 mb-1 text-sm">Secundaire kleur (accent)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        className="h-8 w-8 rounded cursor-pointer border border-theme-10"
                        value={styleSettings.light.secondary_color}
                        onChange={(e) => handleStyleChange('light', 'secondary_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                        value={styleSettings.light.secondary_color}
                        onChange={(e) => handleStyleChange('light', 'secondary_color', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Dark mode */}
                <div className="space-y-4">
                  <h4 className="text-theme font-medium">Dark mode</h4>
                  
                  <div>
                    <label className="block text-theme-80 mb-1 text-sm">Achtergrond afbeelding URL</label>
                    <input
                      type="text"
                      className="w-full bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                      value={styleSettings.dark.background.image || ''}
                      onChange={(e) => handleStyleChange('dark', 'background_image', e.target.value === '' ? null : e.target.value)}
                      placeholder="URL van afbeelding (optioneel)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-theme-80 mb-1 text-sm">Achtergrond kleur</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        className="h-8 w-8 rounded cursor-pointer border border-theme-10"
                        value={styleSettings.dark.background.color}
                        onChange={(e) => handleStyleChange('dark', 'background_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                        value={styleSettings.dark.background.color}
                        onChange={(e) => handleStyleChange('dark', 'background_color', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-theme-80 mb-1 text-sm">Tekst kleur</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        className="h-8 w-8 rounded cursor-pointer border border-theme-10"
                        value={styleSettings.dark.text_color}
                        onChange={(e) => handleStyleChange('dark', 'text_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                        value={styleSettings.dark.text_color}
                        onChange={(e) => handleStyleChange('dark', 'text_color', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-theme-80 mb-1 text-sm">Primaire kleur (widget achtergrond)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        className="h-8 w-8 rounded cursor-pointer border border-theme-10"
                        value={styleSettings.dark.primary_color}
                        onChange={(e) => handleStyleChange('dark', 'primary_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                        value={styleSettings.dark.primary_color}
                        onChange={(e) => handleStyleChange('dark', 'primary_color', e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-theme-80 mb-1 text-sm">Secundaire kleur (accent)</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        className="h-8 w-8 rounded cursor-pointer border border-theme-10"
                        value={styleSettings.dark.secondary_color}
                        onChange={(e) => handleStyleChange('dark', 'secondary_color', e.target.value)}
                      />
                      <input
                        type="text"
                        className="flex-1 bg-primary-theme-10 border border-theme-10 rounded px-3 py-2 text-theme"
                        value={styleSettings.dark.secondary_color}
                        onChange={(e) => handleStyleChange('dark', 'secondary_color', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'layout' && (
            <div>
              <h3 className="text-theme font-medium mb-4">Welcome Widget Layout</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-theme-80 mb-3 text-sm">Kies de layout voor de welcome widget:</label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Centered Layout Option */}
                    <div 
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                        styleSettings.layout === 'centered' 
                          ? 'border-secondary-theme bg-secondary-theme/10' 
                          : 'border-theme-20 hover:border-theme-30'
                      }`}
                      onClick={() => setStyleSettings(prev => ({ ...prev, layout: 'centered' }))}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-theme font-medium">Gecentreerd</h4>
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          styleSettings.layout === 'centered' 
                            ? 'border-secondary-theme bg-secondary-theme' 
                            : 'border-theme-30'
                        }`}>
                          {styleSettings.layout === 'centered' && (
                            <div className="w-full h-full rounded-full bg-white scale-50"></div>
                          )}
                        </div>
                      </div>
                      
                      {/* Preview mockup for centered layout */}
                      <div className="bg-primary-theme-10 rounded p-3 text-center">
                        <div className="text-xs text-theme-80 mb-1">22:40</div>
                        <div className="text-xs text-theme-80 mb-2">Goedenavond, Daniel.</div>
                        <div className="flex justify-center gap-1 mb-2">
                          <div className="w-12 h-4 bg-theme-20 rounded text-xs"></div>
                          <div className="w-12 h-4 bg-theme-20 rounded text-xs"></div>
                          <div className="w-12 h-4 bg-theme-20 rounded text-xs"></div>
                        </div>
                        <div className="text-xs text-theme-60">Klassieke gecentreerde layout</div>
                      </div>
                    </div>

                    {/* Left-aligned Layout Option */}
                    <div 
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                        styleSettings.layout === 'left-aligned' 
                          ? 'border-secondary-theme bg-secondary-theme/10' 
                          : 'border-theme-20 hover:border-theme-30'
                      }`}
                      onClick={() => setStyleSettings(prev => ({ ...prev, layout: 'left-aligned' }))}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-theme font-medium">Links uitgelijnd</h4>
                        <div className={`w-4 h-4 rounded-full border-2 ${
                          styleSettings.layout === 'left-aligned' 
                            ? 'border-secondary-theme bg-secondary-theme' 
                            : 'border-theme-30'
                        }`}>
                          {styleSettings.layout === 'left-aligned' && (
                            <div className="w-full h-full rounded-full bg-white scale-50"></div>
                          )}
                        </div>
                      </div>
                      
                      {/* Preview mockup for left-aligned layout */}
                      <div className="bg-primary-theme-10 rounded p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="text-xs text-theme-80 mb-1">22:40</div>
                            <div className="text-xs text-theme-80">Goedenavond, Daniel.</div>
                          </div>
                          <div className="text-xs text-theme-60">☀️ 20°C</div>
                        </div>
                        <div className="text-xs text-theme-60 mb-2">Agenda vandaag</div>
                        <div className="w-full h-3 bg-theme-20 rounded mb-2"></div>
                        <div className="flex gap-1">
                          <div className="w-8 h-3 bg-theme-20 rounded"></div>
                          <div className="w-8 h-3 bg-theme-20 rounded"></div>
                          <div className="w-8 h-3 bg-theme-20 rounded"></div>
                        </div>
                        <div className="text-xs text-theme-60 mt-1">Dashboard-stijl layout</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-primary-theme-10 rounded-lg">
                  <h5 className="text-theme font-medium mb-2">Layout beschrijving:</h5>
                  {styleSettings.layout === 'centered' ? (
                    <p className="text-theme-80 text-sm">
                      De gecentreerde layout toont de tijd en begroeting in het midden van het scherm, 
                      met quicklinks eronder. Dit is de klassieke, minimalistische weergave.
                    </p>
                  ) : (
                    <p className="text-theme-80 text-sm">
                      De links uitgelijnde layout toont de tijd links, weer rechts, en bevat een agenda sectie 
                      met quicklinks eronder. Dit geeft een dashboard-achtige ervaring met meer informatie.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer met acties */}
        <div className="p-4 border-t border-theme-10 flex justify-between gap-2">
          <div>
            {onLogout && (
              <Button
                variant="outline"
                className="text-destructive border-destructive hover:bg-destructive/10"
                onClick={onLogout}
              >
                Uitloggen
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="text-theme border-theme-20"
              onClick={onClose}
            >
              Annuleren
            </Button>
            <Button
              variant="default"
              className="bg-secondary-theme hover:bg-secondary-theme/80 text-black"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? 'Opslaan...' : 'Opslaan'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}; 